import { ProjectsDataGridApi } from "src/api/generated/erp/project/project/api/projectsDataGridApi";
import { ControlledAsyncFormCrudDataGrid } from "src/components/common/dataGrid/crud/ControlledAsyncFormCrudDataGrid";
import { useServerSideDataGridModel } from "src/components/common/dataGrid/gridModel/useServerSideDataGridModel";
import {
	dateColumn,
	dateTimeColumn,
	enumColumn,
	integerColumn,
	textColumn,
} from "src/components/common/dataGrid/columns.tsx";
import i18n from "i18next";
import { getProjectStateLabels } from "src/api/generated/erp/db/types/enums/projectState.ts";
import { ProjectContextMenu } from "src/components/views/erp/project/project/ProjectContextMenu.tsx";
import { ProjectForm } from "src/components/views/erp/project/project/ProjectForm.tsx";
import { DocumentsOfObjectButton } from "src/components/views/documents/objectDocuments/DocumentsOfObjectButton.tsx";
import { nullableAavoObjectRef } from "src/utils/aavoObjectRefUtils.ts";
import { AavoTextField } from "src/components/common/inputFields/AavoTextField.tsx";
import { MenuCheckbox } from "src/components/common/contextMenu/MenuCheckbox.tsx";
import { ProjectView } from "src/api/generated/erp/db/types/tables/projectView.ts";
import { CreateNewProjectForm } from "src/components/views/erp/project/project/CreateNewProjectForm.tsx";
import { genericNullableValue } from "src/utils/genericNullableValue.ts";
import { AsyncSelectField } from "src/components/common/inputFields/AsyncSelectField.tsx";
import { AppUser } from "src/api/generated/postgres/db/types/role_management/tables/appUser.ts";
import { ProjectQueryApi } from "src/api/generated/erp/project/project/api/projectQueryApi.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface ProjectsDataGridProps {
	onlyProjectId?: number;
	onSelectionChanged?: (project: ProjectView | undefined) => void;
}

export const ProjectsDataGrid = ({ onlyProjectId, onSelectionChanged }: ProjectsDataGridProps) => {
	const { dataGridProps, onlySelectedRow, currentParams, refreshData } = useServerSideDataGridModel({
		fetchData: (params) =>
			ProjectsDataGridApi.getDataGridData({
				onlyProjectId,
				...params,
			}),
		initialParams: {
			searchQuery: "",
			onlyDefaultSiteProjects: true,
			completedProjects: false,
			cancelledProjects: false,
			responsiblePersonId: genericNullableValue<number>(),
		},
		getRowId: (row) => row.projectId,
		customFunctionUiActionView: "PROJECTS",
		gridId: "8A2DF2DCAE543A01",
		onSelectionChanged: (rows) => onSelectionChanged?.(rows[0]),
		selectFirstRowOnLoad: onlyProjectId != null,
	});

	return (
		<ControlledAsyncFormCrudDataGrid<ProjectView>
			columns={[
				integerColumn({
					field: "projectId",
					headerName: i18n.t("number_shortened"),
					width: 80,
				}),
				textColumn({
					field: "siteName",
					headerName: i18n.t("site"),
					width: 80,
				}),
				textColumn({
					field: "projectDescription",
					headerName: i18n.t("name"),
					width: 200,
				}),
				textColumn({
					field: "groupName",
					headerName: i18n.t("group"),
				}),
				textColumn({
					field: "projectType",
					headerName: i18n.t("type"),
				}),
				textColumn({
					field: "projectResponsiblePersonName",
					headerName: i18n.t("responsible_person"),
					width: 120,
				}),
				integerColumn({
					field: "customerOrderId",
					headerName: i18n.t("customer_order"),
				}),
				textColumn({
					field: "comment",
					headerName: i18n.t("comment"),
				}),
				dateColumn({
					field: "createdDate",
					headerName: i18n.t("created_at"),
				}),
				textColumn({
					field: "createdByUserName",
					headerName: i18n.t("created_by"),
				}),
				dateColumn({
					field: "plannedStartDate",
					headerName: i18n.t("planned_start"),
				}),
				dateColumn({
					field: "plannedEndDate",
					headerName: i18n.t("planned_end"),
				}),
				enumColumn({
					field: "projectState",
					headerName: i18n.t("state"),
					enumLabels: getProjectStateLabels(),
				}),
				dateTimeColumn({
					field: "releasedDate",
					headerName: i18n.t("released_at"),
					width: 150,
				}),
				dateTimeColumn({
					field: "completedDate",
					headerName: i18n.t("completed_at"),
					width: 150,
				}),
				dateTimeColumn({
					field: "cancelledDate",
					headerName: i18n.t("cancelled_at"),
					width: 150,
				}),
				textColumn({
					field: "addressName",
					headerName: i18n.t("address_name"),
					width: 120,
				}),
				textColumn({
					field: "address_1",
					headerName: i18n.t("address_1"),
					width: 120,
				}),
				textColumn({
					field: "address_2",
					headerName: i18n.t("address_2"),
					width: 120,
				}),
				textColumn({
					field: "postalCode",
					headerName: i18n.t("postal_code"),
				}),
				textColumn({
					field: "city",
					headerName: i18n.t("city"),
					width: 120,
				}),
				textColumn({
					field: "addressContact",
					headerName: i18n.t("address_contact"),
					width: 150,
				}),
			]}
			rowContextMenuComponents={({ allSelectedRows }) => (
				<ProjectContextMenu allSelectedProjects={allSelectedRows} refreshData={refreshData} />
			)}
			actionBarComponents={
				<>
					<DocumentsOfObjectButton objectRef={nullableAavoObjectRef("PROJECT", onlySelectedRow?.projectId)} />
					<AavoTextField label={i18n.t("search")} onSubmit={(searchQuery) => refreshData({ searchQuery })} />
					<AsyncSelectField<AppUser, number>
						label={i18n.t("responsible_person")}
						fetchOptions={({ searchQuery, currentSelection }) =>
							ProjectQueryApi.getProjectResponsiblePersonOptions({
								currentSelection: currentSelection,
								userNameSearchQuery: searchQuery,
							})
						}
						getOptionKey={(option) => option.id}
						getOptionLabel={(option) => option.name}
						onChange={async (value) => {
							await refreshData({
								responsiblePersonId: value?.id ?? null,
							});
						}}
					/>
				</>
			}
			actionBarMenuComponents={[
				<MenuCheckbox
					key={"onlyDefaultSiteProjects"}
					label={i18n.t("only_default_site")}
					checked={currentParams.onlyDefaultSiteProjects}
					onChange={(checked) => refreshData({ onlyDefaultSiteProjects: checked })}
				/>,
				<MenuCheckbox
					key={"completedProjects"}
					label={i18n.t("show_completed")}
					checked={currentParams.completedProjects}
					onChange={(checked) => refreshData({ completedProjects: checked })}
				/>,
				<MenuCheckbox
					key={"cancelledProjects"}
					label={i18n.t("show_cancelled")}
					checked={currentParams.cancelledProjects}
					onChange={(checked) => refreshData({ cancelledProjects: checked })}
				/>,
			]}
			form={{
				addRowEnabled: true,
				editEnabled: true,
				dialogTitle: (row) => (row == null ? i18n.t("new_project") : i18n.t("project")),
				dialogSize: (row) => (row == null ? "sm" : "lg"),
				agentContext: (row) => ({
					key: defineAgentUiContextKey("erp.project.project.projectsDataGrid.formDialog"),
					data: { projectId: row?.projectId ?? null },
					pathSegments: [row?.projectDescription ?? i18n.t("new_project")],
				}),
				component: ({ row, closeDialog, ...other }) =>
					row == null ?
						<CreateNewProjectForm closeDialog={closeDialog} {...other} />
					:	<ProjectForm projectId={row?.projectId ?? null} {...other} />,
			}}
			agentContext={{
				key: defineAgentUiContextKey("erp.project.projects.grid"),
				data: {
					onlyProjectId: onlyProjectId ?? null,
				},
				rowFields: ["projectId", "projectDescription", "customerOrderId", "projectState"],
				selectedRowPathSegment: (row) => row.projectDescription,
				selectedRowsPathSegment: (count) => i18n.t("projects_selected_count", { count }),
			}}
			{...dataGridProps}
		/>
	);
};
